import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config/config";
import {
  getPreset,
  matchPreset,
  pickRuleFields,
  summarizeSetting,
} from "../constants/propertySettingPresets";

// The search endpoint only tells us *whether* a flat has a rule-set attached
// (`is_property_settings`), never which one. The rules themselves live on the
// landlord's settings, each carrying the property ids it was applied to — so
// the propertyId -> rules mapping has to be rebuilt on the client from
// GET /property-settings/:landlordId.
//
// Search results routinely repeat the same landlord across several flats, and
// the tenant dashboard re-mounts on every navigation, so responses are cached
// per landlord for the lifetime of the tab. Rules change rarely enough that a
// stale read is not worth an extra round-trip per card.
const settingsCache = new Map(); // landlordId -> Promise<Map<propertyId, rules>>

function fetchLandlordRules(landlordId) {
  if (settingsCache.has(landlordId)) return settingsCache.get(landlordId);

  const request = axios
    .get(`${BASE_URL}/property-settings/${landlordId}`)
    .then((res) => {
      const settings = res?.data?.data || res?.data || [];
      const byProperty = new Map();

      (Array.isArray(settings) ? settings : []).forEach((setting) => {
        const rules = pickRuleFields(setting);
        (setting?.properties || []).forEach((propertyId) => {
          byProperty.set(String(propertyId), {
            ...rules,
            settingName: setting?.settingName || "",
          });
        });
      });

      return byProperty;
    })
    .catch(() => {
      // A landlord with no settings 404s on some deployments. Treat any failure
      // as "no rules published" rather than blanking the whole card — the flat
      // itself is still perfectly renderable without them.
      settingsCache.delete(landlordId);
      return new Map();
    });

  settingsCache.set(landlordId, request);
  return request;
}

// The badge shown on a card: which preset the rule-set still matches, with the
// preset's own accent colour. A landlord who tuned anything falls to "Custom".
export function describeRules(rules) {
  if (!rules) return null;

  const key = matchPreset(rules);
  const preset = getPreset(key);

  return {
    key,
    label: preset?.label || "Custom",
    tagline: preset?.tagline || "Tailored terms",
    accent: preset?.accent || "#64748b",
    noticePeriod: rules.tenantNoticePeriod || "",
    penalty: rules.tenantPenalty || "",
    terminationCount: rules.terminationReasons?.length || 0,
    summary: summarizeSetting(rules),
  };
}

/**
 * Resolves rule-sets for a batch of flats.
 *
 * @param {Array<{ _id?: string, id?: string, flatId?: string, landlordId?: string }>} flats
 * @returns {{ rulesByProperty: Map<string, object>, loading: boolean }}
 */
export function usePropertyRules(flats) {
  const [rulesByProperty, setRulesByProperty] = useState(() => new Map());
  const [loading, setLoading] = useState(false);

  // Landlord ids drive the fetches, so the effect must not re-run just because
  // the caller rebuilt the flats array with the same owners in it.
  const landlordKey = useMemo(() => {
    const ids = new Set();
    (flats || []).forEach((flat) => {
      const id = flat?.landlordId || flat?.userId;
      if (id) ids.add(String(id));
    });
    return [...ids].sort().join(",");
  }, [flats]);

  useEffect(() => {
    if (!landlordKey) {
      setRulesByProperty(new Map());
      return;
    }

    let cancelled = false;
    const ids = landlordKey.split(",");

    setLoading(true);
    Promise.all(ids.map(fetchLandlordRules)).then((maps) => {
      if (cancelled) return;

      const merged = new Map();
      maps.forEach((map) => map.forEach((value, key) => merged.set(key, value)));
      setRulesByProperty(merged);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [landlordKey]);

  return { rulesByProperty, loading };
}

/**
 * Single-property variant, for detail views that open one flat at a time.
 */
export function usePropertyRule(landlordId, propertyId) {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!landlordId || !propertyId) {
      setRules(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchLandlordRules(String(landlordId)).then((map) => {
      if (cancelled) return;
      setRules(map.get(String(propertyId)) || null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [landlordId, propertyId]);

  return { rules, loading };
}
